import type { ChatListEntry } from "@/lib/chat";

/**
 * 左ペインのチャット一覧に掛ける絞り込み 1 件。種類ごとに最大 1 件で、並び順は追加順。
 * 無い種類は絞り込まない。ただし status だけは「無い = 有効なチャットのみ」で、
 * アーカイブ済みは明示的に選んだときだけ表示する。
 */
export type ChatListFilter =
	| { kind: "visibility"; value: ChatVisibilityFilterValue }
	| { kind: "status"; value: ChatStatusFilterValue };

export type ChatListFilterKind = ChatListFilter["kind"];
export type ChatVisibilityFilterValue = "all" | "mine";
export type ChatStatusFilterValue = "all" | "archived";

export const CHAT_LIST_FILTER_KINDS: readonly ChatListFilterKind[] = ["visibility", "status"];

export const CHAT_LIST_FILTER_KIND_LABELS: Record<ChatListFilterKind, string> = {
	visibility: "リスト表示",
	status: "ステータス",
};

export const CHAT_VISIBILITY_FILTER_OPTIONS: readonly {
	value: ChatVisibilityFilterValue;
	label: string;
}[] = [
	{ value: "mine", label: "自分のみ" },
	{ value: "all", label: "組織全体" },
];

export const CHAT_STATUS_FILTER_OPTIONS: readonly {
	value: ChatStatusFilterValue;
	label: string;
}[] = [
	{ value: "all", label: "すべて" },
	{ value: "archived", label: "アーカイブ済み" },
];

/** 初回起動時の絞り込み。自分のチャットだけを表示する。 */
export const DEFAULT_CHAT_LIST_FILTERS: readonly ChatListFilter[] = [
	{ kind: "visibility", value: "mine" },
];

function isVisibilityValue(value: unknown): value is ChatVisibilityFilterValue {
	return value === "all" || value === "mine";
}

function isStatusValue(value: unknown): value is ChatStatusFilterValue {
	return value === "all" || value === "archived";
}

function parseChatListFilter(raw: unknown): ChatListFilter | null {
	if (raw === null || typeof raw !== "object") return null;
	const { kind, value } = raw as { kind?: unknown; value?: unknown };
	switch (kind) {
		case "visibility":
			return isVisibilityValue(value) ? { kind, value } : null;
		case "status":
			return isStatusValue(value) ? { kind, value } : null;
		default:
			return null;
	}
}

/**
 * localStorage に保存した JSON を絞り込み一覧に戻す。壊れた項目・不明な種類・重複した種類は
 * 捨てる。保存が無い (null) 場合は初期値を返す。
 */
export function parseStoredChatListFilters(raw: string | null): ChatListFilter[] {
	if (raw === null) return [...DEFAULT_CHAT_LIST_FILTERS];
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return [...DEFAULT_CHAT_LIST_FILTERS];
	}
	if (!Array.isArray(parsed)) return [...DEFAULT_CHAT_LIST_FILTERS];
	const filters: ChatListFilter[] = [];
	for (const entry of parsed) {
		const filter = parseChatListFilter(entry);
		if (filter && !filters.some((f) => f.kind === filter.kind)) filters.push(filter);
	}
	return filters;
}

/** 同じ種類があればその位置で値を置き換え、無ければ末尾に追加する。 */
export function upsertChatListFilter(
	filters: readonly ChatListFilter[],
	filter: ChatListFilter,
): ChatListFilter[] {
	if (!filters.some((f) => f.kind === filter.kind)) return [...filters, filter];
	return filters.map((f) => (f.kind === filter.kind ? filter : f));
}

export function removeChatListFilter(
	filters: readonly ChatListFilter[],
	kind: ChatListFilterKind,
): ChatListFilter[] {
	return filters.filter((f) => f.kind !== kind);
}

export function findChatListFilter<K extends ChatListFilterKind>(
	filters: readonly ChatListFilter[],
	kind: K,
): Extract<ChatListFilter, { kind: K }> | undefined {
	return filters.find((f): f is Extract<ChatListFilter, { kind: K }> => f.kind === kind);
}

function matchesChatListFilter(
	chat: ChatListEntry,
	filter: ChatListFilter,
	currentUserId: string | null,
): boolean {
	switch (filter.kind) {
		case "visibility":
			return filter.value === "all" || currentUserId === null || chat.createdById === currentUserId;
		case "status":
			return filter.value === "all" || chat.archived;
	}
}

export function matchesChatListFilters(
	chat: ChatListEntry,
	filters: readonly ChatListFilter[],
	currentUserId: string | null,
): boolean {
	if (!findChatListFilter(filters, "status") && chat.archived) return false;
	return filters.every((filter) => matchesChatListFilter(chat, filter, currentUserId));
}

export function applyChatListFilters(
	chats: readonly ChatListEntry[],
	filters: readonly ChatListFilter[],
	currentUserId: string | null,
): ChatListEntry[] {
	return chats.filter((chat) => matchesChatListFilters(chat, filters, currentUserId));
}

/**
 * 直接開いた (ディープリンク等の) チャットが一覧に現れるよう、隠している絞り込みだけを緩める。
 * 他人のチャットなら組織全体へ、アーカイブ済みならアーカイブ済み表示へ切り替える。
 */
export function filtersRevealingChat(
	filters: ChatListFilter[],
	chat: Pick<ChatListEntry, "createdById" | "archived">,
	currentUserId: string | null,
): ChatListFilter[] {
	let next = filters;
	const visibility = findChatListFilter(next, "visibility");
	if (visibility?.value === "mine" && currentUserId && chat.createdById !== currentUserId) {
		next = upsertChatListFilter(next, { kind: "visibility", value: "all" });
	}
	if (chat.archived && !findChatListFilter(next, "status")) {
		next = upsertChatListFilter(next, { kind: "status", value: "archived" });
	}
	return next;
}
